import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Lock, CreditCard, ShieldCheck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { FALLBACK_PRODUCT_IMAGE } from '../data/products';
export function Checkout() {
  const {
    state,
    cartTotal,
    dispatch
  } = useCart();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [shippingMethod, setShippingMethod] = useState<'standard' | 'express'>('standard');
  const shippingCost = shippingMethod === 'standard' ? 0 : 50;
  const finalTotal = cartTotal + shippingCost;
  if (state.items.length === 0 && !isProcessing) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center" data-id="element-226">
        <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-227">
          Your cart is empty
        </h2>
        <button onClick={() => navigate('/shop')} className="text-red-600 hover:underline" data-id="element-228">
          Return to Shop
        </button>
      </div>;
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    // Simulate BML Gateway redirect and processing
    setTimeout(() => {
      dispatch({
        type: 'CLEAR_CART'
      });
      navigate('/order-confirmation', {
        state: {
          orderNumber: `BS-${Math.floor(10000 + Math.random() * 90000)}`,
          total: finalTotal
        }
      });
    }, 2500);
  };
  if (isProcessing) {
    return <div className="min-h-[80vh] flex flex-col items-center justify-center bg-gray-50" data-id="element-229">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center max-w-md w-full" data-id="element-230">
          <img src="https://cdn.magicpatterns.com/uploads/2e3rWSAHwSB4NuxAx1sgGW/Logo_of_Bank_of_Maldives.svg.png" alt="Bank of Maldives" className="w-16 h-16 object-contain mx-auto mb-4" data-id="element-231" />
          <div className="w-10 h-10 mx-auto mb-6" data-id="element-232">
            <div className="w-10 h-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin" data-id="element-233"></div>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-234">
            Redirecting to Bank of Maldives…
          </h2>
          <p className="text-gray-500 text-sm" data-id="element-235">
            Please do not close this window or press back.
          </p>

          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-center gap-2 text-gray-400" data-id="element-236">
            <Lock className="w-4 h-4" data-id="element-237" />
            <span className="text-xs font-medium uppercase tracking-wider" data-id="element-238">
              Secure Payment Gateway
            </span>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen pb-12" data-id="element-239">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-240">
        <Link to="/cart" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-8" data-id="element-241">
          <ChevronLeft className="w-4 h-4 mr-1" data-id="element-242" /> Back to Cart
        </Link>

        <div className="lg:grid lg:grid-cols-12 lg:gap-x-12" data-id="element-243">
          {/* Left Column - Forms */}
          <div className="lg:col-span-7 xl:col-span-8" data-id="element-244">
            <form id="checkout-form" onSubmit={handleSubmit} className="space-y-8" data-id="element-245">
              {/* Contact Info */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-246">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-247">
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-248">
                  <div className="sm:col-span-2" data-id="element-249">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-250">
                      Email address
                    </label>
                    <input type="email" id="email" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-251" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-252">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-253">
                      Phone number
                    </label>
                    <div className="flex" data-id="element-254">
                      <span className="inline-flex items-center px-4 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-255">
                        +960
                      </span>
                      <input type="tel" id="phone" required placeholder="7XXXXXX" pattern="[79][0-9]{6}" className="flex-1 min-w-0 block w-full px-4 py-2 rounded-none rounded-r-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-256" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-257">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-258">
                  Shipping Address
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-259">
                  <div data-id="element-260">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-261">
                      First name
                    </label>
                    <input type="text" id="firstName" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-262" />
                  </div>
                  <div data-id="element-263">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-264">
                      Last name
                    </label>
                    <input type="text" id="lastName" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-265" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-266">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-267">
                      Address
                    </label>
                    <input type="text" id="address" required placeholder="House name, Street" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-268" />
                  </div>
                  <div data-id="element-269">
                    <label htmlFor="island" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-270">
                      Island / City
                    </label>
                    <select id="island" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white" data-id="element-271">
                      <option value="" data-id="element-272">Select an island...</option>
                      <option value="male" data-id="element-273">Malé</option>
                      <option value="hulhumale" data-id="element-274">Hulhumalé</option>
                      <option value="villingili" data-id="element-275">Villingili</option>
                      <option value="addu" data-id="element-276">Addu City</option>
                      <option value="fuvahmulah" data-id="element-277">Fuvahmulah</option>
                      <option value="kulhudhuffushi" data-id="element-278">Kulhudhuffushi</option>
                    </select>
                  </div>
                  <div data-id="element-279">
                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-280">
                      Postal code
                    </label>
                    <input type="text" id="postalCode" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-281" />
                  </div>
                </div>
              </div>

              {/* Shipping Method */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-282">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-283">
                  Shipping Method
                </h2>
                <div className="space-y-3" data-id="element-284">
                  <label className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${shippingMethod === 'standard' ? 'border-red-600 bg-red-50/30' : 'border-gray-200'}`} data-id="element-285">
                    <div className="flex items-center gap-3" data-id="element-286">
                      <input type="radio" name="shipping" value="standard" checked={shippingMethod === 'standard'} onChange={() => setShippingMethod('standard')} className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300" data-id="element-287" />
                      <div data-id="element-288">
                        <span className="block text-sm font-medium text-gray-900" data-id="element-289">
                          Standard Delivery
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-290">
                          2-3 business days
                        </span>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900" data-id="element-291">
                      Free
                    </span>
                  </label>

                  <label className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${shippingMethod === 'express' ? 'border-red-600 bg-red-50/30' : 'border-gray-200'}`} data-id="element-292">
                    <div className="flex items-center gap-3" data-id="element-293">
                      <input type="radio" name="shipping" value="express" checked={shippingMethod === 'express'} onChange={() => setShippingMethod('express')} className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300" data-id="element-294" />
                      <div data-id="element-295">
                        <span className="block text-sm font-medium text-gray-900" data-id="element-296">
                          Express Delivery
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-297">
                          Same day / Next day
                        </span>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900" data-id="element-298">
                      MVR 50.00
                    </span>
                  </label>
                </div>
              </div>

              {/* Payment Section */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-299">
                <div className="flex items-center gap-2 mb-4" data-id="element-300">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-301">
                    Payment
                  </h2>
                  <Lock className="w-4 h-4 text-gray-400" data-id="element-302" />
                </div>
                <p className="text-sm text-gray-500 mb-4" data-id="element-303">
                  All transactions are secure and encrypted.
                </p>

                <div className="border border-red-200 bg-red-50/30 rounded-xl p-5 relative overflow-hidden" data-id="element-304">
                  <div className="flex items-start gap-4" data-id="element-305">
                    <input type="radio" checked readOnly className="w-4 h-4 mt-1 text-red-600 focus:ring-red-500 border-gray-300 flex-shrink-0" data-id="element-306" />
                    <div className="flex-1" data-id="element-307">
                      <div className="flex items-center gap-3 mb-2" data-id="element-308">
                        <img src="https://cdn.magicpatterns.com/uploads/2e3rWSAHwSB4NuxAx1sgGW/Logo_of_Bank_of_Maldives.svg.png" alt="Bank of Maldives" className="w-10 h-10 object-contain" data-id="element-309" />
                        <div data-id="element-310">
                          <div className="font-medium text-gray-900" data-id="element-311">
                            Bank of Maldives Gateway
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-312">
                            Pay securely with BML
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3 mt-3" data-id="element-313">
                        You will be redirected to the secure BML payment gateway
                        to complete your purchase.
                      </p>
                      <div className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 bg-white px-3 py-1.5 rounded-md border border-gray-200" data-id="element-314">
                        <ShieldCheck className="w-4 h-4 text-green-600" data-id="element-315" />
                        BML Secure Checkout
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-5 xl:col-span-4 mt-8 lg:mt-0" data-id="element-316">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 sticky top-24" data-id="element-317">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-318">
                Order Summary
              </h2>

              <div className="flow-root mb-6" data-id="element-319">
                <ul className="-my-4 divide-y divide-gray-100" data-id="element-320">
                  {state.items.map(item => <li key={item.id} className="py-4 flex gap-4" data-id="element-321">
                      <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden border border-gray-200 flex-shrink-0 relative" data-id="element-322">
                        <img src={item.product.images[0]} alt={item.product.name} onError={e => {
                      const el = e.currentTarget;
                      if (el.src !== FALLBACK_PRODUCT_IMAGE) el.src = FALLBACK_PRODUCT_IMAGE;
                    }} className="w-full h-full object-cover" data-id="element-323" />
                        <span className="absolute -top-2 -right-2 w-5 h-5 bg-gray-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white" data-id="element-324">
                          {item.quantity}
                        </span>
                      </div>
                      <div className="flex-1 flex flex-col justify-center" data-id="element-325">
                        <h3 className="text-sm font-medium text-gray-900 line-clamp-1" data-id="element-326">
                          {item.product.name}
                        </h3>
                        <div className="text-xs text-gray-500 mt-0.5" data-id="element-327">
                          {item.selectedSize && <span data-id="element-328">Size: {item.selectedSize}</span>}
                          {item.selectedSize && item.selectedColor && <span data-id="element-329"> | </span>}
                          {item.selectedColor && <span data-id="element-330">Color: {item.selectedColor.name}</span>}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-900 flex items-center" data-id="element-331">
                        MVR {(item.unitPrice * item.quantity).toFixed(2)}
                      </div>
                    </li>)}
                </ul>
              </div>

              <div className="space-y-3 border-t border-gray-100 pt-4 mb-6" data-id="element-332">
                <div className="flex justify-between text-sm text-gray-600" data-id="element-333">
                  <span data-id="element-334">Subtotal</span>
                  <span data-id="element-335">MVR {cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600" data-id="element-336">
                  <span data-id="element-337">Shipping</span>
                  <span data-id="element-338">
                    {shippingCost === 0 ? 'Free' : `MVR ${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between text-base font-bold text-gray-900 pt-3 border-t border-gray-100" data-id="element-339">
                  <span data-id="element-340">Total</span>
                  <span data-id="element-341">MVR {finalTotal.toFixed(2)}</span>
                </div>
              </div>

              <button type="submit" form="checkout-form" className="w-full bg-red-600 text-white py-4 px-4 rounded-xl font-medium hover:bg-red-700 transition-colors flex justify-center items-center gap-2 shadow-sm" data-id="element-342">
                <Lock className="w-4 h-4" data-id="element-343" />
                Checkout with Bank of Maldives
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}